export enum KEY_CODE {
    KEY_BACKSPACE = 8,
    KEY_ENTER = 13,
    KEY_ESCAPE = 27,
    KEY_ARROW_LEFT = 37,
    KEY_ARROW_UP = 38,
    KEY_ARROW_RIGHT = 39,
    KEY_ARROW_DOWN = 40,
    KEY_DELETE = 46,
    KEY_0 = 48,
    KEY_1 = 49,
    KEY_2 = 50,
    KEY_3 = 51,
    KEY_4 = 52,
    KEY_5 = 53,
    KEY_6 = 54,
    KEY_7 = 55,
    KEY_8 = 56,
    KEY_9 = 57,
    KEY_CONTROL = 17,
    KEY_COMMAND = 91,
}

export enum KEY_NAME {
    KEY_ENTER = 'Enter',
    KEY_COMMAND = 'Meta',
    KEY_CONTROL = 'Control',
    KEY_BACKSPACE = 'Backspace',
    KEY_ARROW_UP = 'ArrowUp',
    KEY_ARROW_DOWN = 'ArrowDown',
}

interface IEventOption {
    preventDefault?: boolean;
    stopPropagation?: boolean;
}

const singleKeyboardEvent = (
    element: HTMLElement,
    eventName: string,
    keyName: string,
    callback: (e: KeyboardEvent) => any,
    option: IEventOption = {},
) => {
    const handle = ((e: KeyboardEvent) => {
        const precondition = e.key || e.keyCode;
        if (precondition === KEY_NAME[keyName as keyof typeof KEY_NAME] || precondition === KEY_CODE[keyName as keyof typeof KEY_CODE]) {
            callback(e);
            if (option.stopPropagation) {
                e.stopPropagation();
            }
            if (option.preventDefault) {
                e.preventDefault();
                e.returnValue = false;
            }
        }
    }) as EventListener;

    element.addEventListener(eventName, handle);

    return () => {
        element.removeEventListener(eventName, handle);
    };
};

/**
 * @desc 键盘事件处理
 * @author kody
 * @param (element: HTMLElement, callback: (e: KeyboardEvent) => void, option: IEventOption = {}) => () => void
 */
export const keyboardEvent = {
    backspaceDown: (element: HTMLElement, callback: (e: KeyboardEvent) => void, option: IEventOption = {}) => {
        return singleKeyboardEvent(element, 'keydown', 'KEY_BACKSPACE', callback, option);
    },

    arrowDownDown: (element: HTMLElement, callback: (e: KeyboardEvent) => void, option: IEventOption = {}) => {
        return singleKeyboardEvent(element, 'keydown', 'KEY_ARROW_DOWN', callback, option);
    },

    arrowUpDown: (element: HTMLElement, callback: (e: KeyboardEvent) => void, option: IEventOption = {}) => {
        return singleKeyboardEvent(element, 'keydown', 'KEY_ARROW_UP', callback, option);
    },

    enterDown: (element: HTMLElement, callback: (e: KeyboardEvent) => void, option: IEventOption = {}) => {
        let combination = false

        const downHandle = (e: KeyboardEvent) => {
            const keyCode = e.which || e.keyCode;
            const isEnter = e.key === KEY_NAME.KEY_ENTER || keyCode === KEY_CODE.KEY_ENTER;
            if (isEnter && !combination) {
                callback(e);
                if (option.stopPropagation) {
                    e.stopPropagation();
                }
                if (option.preventDefault) {
                    e.preventDefault();
                    e.returnValue = false;
                }
            } else {
                combination = true;
            }
        };

        const upHandle = () => {
            combination = false;
        }

        element.addEventListener('keydown', downHandle);
        element.addEventListener('keyup', upHandle);

        return () => {
            element.removeEventListener('keydown', downHandle);
            element.removeEventListener('keyup', upHandle);
        };
    },
    enterUp: (element: HTMLElement, callback: (e: KeyboardEvent) => void) => {
        let keyDownList: Array<string | number> = [];

        const downHandle = (e: KeyboardEvent) => {
            keyDownList.push(e.key || e.keyCode);
        };

        const upHandle = (e: KeyboardEvent) => {
            const keyCode = e.which || e.keyCode;
            const isEnter = e.key === KEY_NAME.KEY_ENTER || keyCode === KEY_CODE.KEY_ENTER;
            const precondition = keyDownList[0];
            const hasControl =
                precondition &&
                (precondition === KEY_NAME.KEY_CONTROL ||
                    precondition === KEY_CODE.KEY_CONTROL ||
                    precondition === KEY_NAME.KEY_COMMAND ||
                    precondition === KEY_CODE.KEY_COMMAND);
            if (isEnter && !hasControl) {
                callback(e);
                keyDownList = [];
                e.preventDefault();
                return false;
            }
            keyDownList = keyDownList.filter((v) => v !== (e.key || e.keyCode));
            return true;
        }

        element.addEventListener('keydown', downHandle);
        element.addEventListener('keyup', upHandle);

        return () => {
            element.removeEventListener('keydown', downHandle);
            element.removeEventListener('keyup', upHandle);
        };
    },
    controlEnterUp: (element: HTMLElement, callback: (e: KeyboardEvent) => void) => {
        let keyDownList: KEY_NAME[] = [];

        const downHandle = (e: KeyboardEvent) => {
            const keyCode = e.which || e.keyCode;
            const isEnter = e.key === KEY_NAME.KEY_ENTER || keyCode === KEY_CODE.KEY_ENTER;
            const isControl = e.key === KEY_NAME.KEY_CONTROL || keyCode === KEY_CODE.KEY_CONTROL;
            const isCommand = e.key === KEY_NAME.KEY_COMMAND || keyCode === KEY_CODE.KEY_COMMAND;
            if (isControl || isCommand) {
                keyDownList.push(KEY_NAME.KEY_CONTROL);
            } else if (isEnter) {
                keyDownList.push(KEY_NAME.KEY_ENTER);
            }
        };

        const upHandle = (e: KeyboardEvent) => {
            const keyCode = e.which || e.keyCode;
            const isEnter = e.key === KEY_NAME.KEY_ENTER || keyCode === KEY_CODE.KEY_ENTER;
            const isControl = e.key === KEY_NAME.KEY_CONTROL || keyCode === KEY_CODE.KEY_CONTROL;
            const isCommand = e.key === KEY_NAME.KEY_COMMAND || keyCode === KEY_CODE.KEY_COMMAND;
            if (isEnter || isControl || isCommand) {
                if (
                    keyDownList.indexOf(KEY_NAME.KEY_CONTROL) !== -1 &&
                    keyDownList.indexOf(KEY_NAME.KEY_ENTER) !== -1
                ) {
                    callback(e);
                }
            }
            if (isEnter) {
                keyDownList = keyDownList.filter((v) => v !== KEY_NAME.KEY_ENTER);
            }
            if (isControl || isCommand) {
                keyDownList = keyDownList.filter((v) => v !== KEY_NAME.KEY_CONTROL);
            }
        };

        element.addEventListener('keydown', downHandle);
        element.addEventListener('keyup', upHandle);

        return () => {
            element.removeEventListener('keydown', downHandle);
            element.removeEventListener('keyup', upHandle);
        };
    },
};
